import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { translations, type Locale } from './translations'

type I18nContextValue = { locale: Locale; setLocale: (locale: Locale) => void; t: (key: string) => string }
const I18nContext = createContext<I18nContextValue | undefined>(undefined)

function resolve(locale: Locale, key: string) {
  return key.split('.').reduce<unknown>((value, segment) => (value as Record<string, unknown>)?.[segment], translations[locale]) as string | undefined
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(() => (localStorage.getItem('locale') as Locale) || 'zh')
  const value = useMemo(() => ({
    locale,
    setLocale: (next: Locale) => { localStorage.setItem('locale', next); setLocale(next) },
    t: (key: string) => resolve(locale, key) ?? key,
  }), [locale])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n must be used within I18nProvider')
  return context
}
